import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

// Get endpoints — in production use relative paths (same CloudFront domain)
const GRAPHQL_ENDPOINT = import.meta.env.PROD
  ? '/graphql'
  : (import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:4000/graphql')
const AUTH_ENDPOINT = import.meta.env.PROD
  ? '/api'
  : (import.meta.env.VITE_AUTH_ENDPOINT || '')

// HTTP link for GraphQL requests
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
})

// Auth link to add JWT token from auth lambda
const authLink = setContext(async (_, previousContext) => {
  try {
    // Call auth lambda to get JWT token from cookie
    const res = await fetch(`${AUTH_ENDPOINT}/token`, {
      method: 'GET',
      credentials: 'include'
    });
    
    if (res.ok) {
      const { token } = await res.json();
      
      return {
        headers: {
          ...previousContext.headers,
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      };
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  
  // If no token, return headers without Authorization
  return {
    headers: {
      ...previousContext.headers,
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
    }
  };
});

// Error link for handling GraphQL and network errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
      
      // Handle specific error types
      if (message.includes('Unauthorized') || message.includes('Authentication')) {
        // Redirect to login on auth errors
        window.location.href = '/login'
      }
    })
  }

  if (networkError) {
    console.error(`Network error: ${networkError}`)
    
    // Handle network errors
    if ('statusCode' in networkError) {
      switch (networkError.statusCode) {
        case 401:
          // Unauthorized - redirect to login
          window.location.href = '/login'
          break
        case 403:
          // Forbidden - show error message
          console.error('Access forbidden')
          break
        case 500:
          // Server error - show generic error
          console.error('Server error occurred')
          break
      }
    }
  }
})

// Cache configuration
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        listProjects: {
          keyArgs: ['filter'],
          merge(_existing = { items: [], nextToken: null, total: 0 }, incoming) {
            return {
              ...incoming,
              items: [..._existing.items, ...incoming.items],
            }
          },
        },
        listCompanies: {
          keyArgs: [],
          merge(_existing = { items: [], nextToken: null, total: 0 }, incoming) {
            return {
              ...incoming,
              items: [..._existing.items, ...incoming.items],
            }
          },
        },
      },
    },
    Project: {
      fields: {
        isFavorite: {
          read(existing) {
            return existing ?? false
          },
        },
      },
    },
    Owner: {
      keyFields: false, // Don't normalize by id — avoid merging with Operator cache entries
    },
    User: {
      fields: {
        favorites: {
          merge(_existing = [], incoming) {
            return incoming
          },
        },
      },
    },
  },
})

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  connectToDevTools: import.meta.env.DEV,
})

// Helper function to clear cache and reset store
export const resetApolloStore = async () => {
  await apolloClient.clearStore()
  await apolloClient.resetStore()
}